import React, { useState } from 'react';
import type { MedicationCase, CaseDetails, CasePredicate, AgeUnit, MedicationKind } from '../types';
import DeleteConfirmationDialog from '../../../components/dialogs/DeleteConfirmationDialog';

interface Props {
  cases: MedicationCase[] | null;
  kind: MedicationKind;
  onChange: (cases: MedicationCase[] | null) => void;
}

type Tab = 'weight' | 'age';

const ageUnits: AgeUnit[] = ['days', 'months', 'years'];

const CasesEditor: React.FC<Props> = ({ cases, kind, onChange }) => {
  const [tab, setTab] = useState<Tab>(() => {
    if (cases && cases.length > 0) return cases[0].predicate.type as Tab;
    return 'weight';
  });
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState(() => JSON.stringify(cases ?? [], null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [pendingRemoveIdx, setPendingRemoveIdx] = useState<number | null>(null);

  const list = cases ?? [];

  const replaceCase = (idx: number, next: MedicationCase) => {
    const updated = list.map((c, i) => i === idx ? next : c);
    onChange(updated);
  };

  const addCase = () => {
    const blank: MedicationCase = tab === 'weight'
      ? { predicate: { type: 'weight' }, details: {} }
      : { predicate: { type: 'age', unit: 'years' }, details: {} };
    onChange([...(list || []), blank]);
  };

  const removeCase = (idx: number) => {
    setPendingRemoveIdx(idx);
  };

  const confirmRemoveCase = () => {
    if (pendingRemoveIdx === null) return;
    const next = list.filter((_, i) => i !== pendingRemoveIdx);
    onChange(next.length === 0 ? null : next);
    setPendingRemoveIdx(null);
  };

  const moveCase = (idx: number, delta: number) => {
    const target = idx + delta;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    const [item] = next.splice(idx, 1);
    next.splice(target, 0, item);
    onChange(next);
  };

  const onApplyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) {
        setJsonError('הקלט צריך להיות מערך של תנאים.');
        return;
      }
      // Lightweight validation: each item must have predicate + details
      for (const item of parsed) {
        if (!item || typeof item !== 'object') {
          setJsonError('כל איבר חייב להיות אובייקט.');
          return;
        }
        if (!item.predicate || !item.details) {
          setJsonError('כל איבר חייב לכלול שדות predicate ו-details.');
          return;
        }
      }
      setJsonError(null);
      onChange(parsed.length === 0 ? null : parsed);
    } catch (err: any) {
      setJsonError(err.message || 'JSON לא תקין');
    }
  };

  return (
    <div className="resus-admin__cases">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <strong>תנאים (מינון לפי משקל/גיל)</strong>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="resus-admin__btn resus-admin__btn--ghost"
            onClick={() => setShowJson(s => !s)}
            style={{ padding: '4px 10px', fontSize: '0.85rem' }}
          >
            {showJson ? 'סגור JSON' : 'תצוגת JSON'}
          </button>
        </div>
      </div>

      {showJson ? (
        <div className="resus-admin__field">
          <textarea
            className="resus-admin__textarea"
            rows={12}
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
          />
          {jsonError && <span className="resus-admin__field-error">{jsonError}</span>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="resus-admin__btn resus-admin__btn--accent" onClick={onApplyJson}>
              החלף בערך מה-JSON
            </button>
            <button
              type="button"
              className="resus-admin__btn resus-admin__btn--ghost"
              onClick={() => { setJsonText(JSON.stringify(list, null, 2)); setJsonError(null); }}
            >
              איפוס
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="resus-admin__cases__tabs">
            {(['weight', 'age'] as Tab[]).map(t => (
              <button
                key={t}
                type="button"
                className={`resus-admin__chip${tab === t ? ' resus-admin__chip--active' : ''}`}
                onClick={() => setTab(t)}
              >
                {t === 'weight' ? 'תנאי משקל' : 'תנאי גיל'}
              </button>
            ))}
          </div>

          {list.filter(c => c.predicate.type === tab).length === 0 && (
            <p className="resus-admin__subtle">אין תנאים מהסוג הזה.</p>
          )}

          {list.map((c, idx) => {
            if (c.predicate.type !== tab) return null;
            return (
              <CaseRow
                key={idx}
                index={idx}
                value={c}
                kind={kind}
                onChange={(next) => replaceCase(idx, next)}
                onMove={(delta) => moveCase(idx, delta)}
                onRemove={() => removeCase(idx)}
              />
            );
          })}

          <button
            type="button"
            className="resus-admin__btn resus-admin__btn--ghost"
            onClick={addCase}
            style={{ marginTop: 8 }}
          >
            + הוסף {tab === 'weight' ? 'תנאי משקל' : 'תנאי גיל'}
          </button>
        </>
      )}

      <DeleteConfirmationDialog
        open={pendingRemoveIdx !== null}
        onClose={() => setPendingRemoveIdx(null)}
        onConfirm={confirmRemoveCase}
        title="להסיר תנאי זה?"
        showError={false}
        errorMessage=""
        onCloseError={() => {}}
      />
    </div>
  );
};

interface CaseRowProps {
  index: number;
  value: MedicationCase;
  kind: MedicationKind;
  onChange: (next: MedicationCase) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}

const CaseRow: React.FC<CaseRowProps> = ({ index, value, kind, onChange, onMove, onRemove }) => {
  const updatePredicate = (patch: Partial<CasePredicate>) => {
    onChange({ ...value, predicate: { ...value.predicate, ...patch } });
  };

  const updateDetails = (patch: Partial<CaseDetails>) => {
    onChange({ ...value, details: { ...value.details, ...patch } });
  };

  const isWeight = value.predicate.type === 'weight';
  const isAge = value.predicate.type === 'age';

  return (
    <div className="resus-admin__cases__row">
      <div className="resus-admin__cases__row-header">
        <span>תנאי #{index + 1}</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" onClick={() => onMove(-1)} className="resus-admin__btn resus-admin__btn--ghost"
            style={{ padding: '2px 8px', fontSize: '0.85rem' }}>↑</button>
          <button type="button" onClick={() => onMove(1)} className="resus-admin__btn resus-admin__btn--ghost"
            style={{ padding: '2px 8px', fontSize: '0.85rem' }}>↓</button>
          <button type="button" onClick={onRemove} className="resus-admin__delete-btn" style={{ padding: '2px 8px', fontSize: '0.85rem' }}>הסר</button>
        </div>
      </div>

      <div className="resus-admin__cases__predicate-row">
        {isWeight && (
          <>
            <NumberField
              label="משקל מינימלי (ק״ג)"
              value={value.predicate.minWeight ?? null}
              onChange={(n) => updatePredicate({ minWeight: n })}
            />
            <NumberField
              label="משקל מקסימלי (ק״ג)"
              value={value.predicate.maxWeight ?? null}
              onChange={(n) => updatePredicate({ maxWeight: n })}
            />
            <div />
          </>
        )}
        {isAge && (
          <>
            <NumberField
              label={`גיל מינימלי (${value.predicate.unit ?? 'years'})`}
              value={value.predicate.minAge ?? null}
              onChange={(n) => updatePredicate({ minAge: n })}
            />
            <NumberField
              label={`גיל מקסימלי (${value.predicate.unit ?? 'years'})`}
              value={value.predicate.maxAge ?? null}
              onChange={(n) => updatePredicate({ maxAge: n })}
            />
            <div className="resus-admin__field">
              <label className="resus-admin__field-label">יחידה</label>
              <select
                className="resus-admin__select"
                value={value.predicate.unit ?? 'years'}
                onChange={(e) => updatePredicate({ unit: e.target.value as AgeUnit })}
              >
                {ageUnits.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </>
        )}
      </div>

      <hr style={{ border: 'none', borderTop: '1px dashed #DDE5EE' }} />

      {kind === 'drug' ? (
        <DrugDetailsFields details={value.details} onChange={updateDetails} />
      ) : (
        <DripDetailsFields details={value.details} onChange={updateDetails} />
      )}
    </div>
  );
};

interface NumberFieldProps {
  label: string;
  value: number | null;
  onChange: (val: number | null) => void;
}

const NumberField: React.FC<NumberFieldProps> = ({ label, value, onChange }) => {
  return (
    <div className="resus-admin__field">
      <label className="resus-admin__field-label">{label}</label>
      <input
        className="resus-admin__input"
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
    </div>
  );
};

const DrugDetailsFields: React.FC<{ details: CaseDetails; onChange: (p: Partial<CaseDetails>) => void }> = ({ details, onChange }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
    <NumberField label="dose_per_kg" value={details.dose_per_kg ?? null} onChange={(n) => onChange({ dose_per_kg: n })} />
    <NumberField label="constant_dose" value={details.constant_dose ?? null} onChange={(n) => onChange({ constant_dose: n })} />
    <TextField label="dose_unit" value={details.dose_unit ?? ''} onChange={(s) => onChange({ dose_unit: s || null })} />
    <TextField label="concentration" value={details.concentration ?? ''} onChange={(s) => onChange({ concentration: s || null })} />
    <TextField label="maxDose" value={details.maxDose ?? ''} onChange={(s) => onChange({ maxDose: s || null })} />
    <TextField label="minDose" value={details.minDose ?? ''} onChange={(s) => onChange({ minDose: s || null })} />
    <TextAreaField label="comment" value={details.comment ?? ''} onChange={(s) => onChange({ comment: s || null })} />
    <TextAreaField label="warnText" value={details.warnText ?? ''} onChange={(s) => onChange({ warnText: s || null })} />
  </div>
);

const DripDetailsFields: React.FC<{ details: CaseDetails; onChange: (p: Partial<CaseDetails>) => void }> = ({ details, onChange }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
    <NumberField label="dose_per_kg_per_min" value={details.dose_per_kg_per_min ?? null} onChange={(n) => onChange({ dose_per_kg_per_min: n })} />
    <NumberField label="dose_per_kg_per_hour" value={details.dose_per_kg_per_hour ?? null} onChange={(n) => onChange({ dose_per_kg_per_hour: n })} />
    <NumberField label="default_dilution_volume_ml" value={details.default_dilution_volume_ml ?? null} onChange={(n) => onChange({ default_dilution_volume_ml: n })} />
    <NumberField label="target_volume_ml_per_hour" value={details.target_volume_ml_per_hour ?? null} onChange={(n) => onChange({ target_volume_ml_per_hour: n })} />
    <TextField label="existing_dilution_concentration" value={details.existing_dilution_concentration ?? ''} onChange={(s) => onChange({ existing_dilution_concentration: s || null })} />
    <TextField label="existing_dilution_concentration_dose_unit" value={details.existing_dilution_concentration_dose_unit ?? ''} onChange={(s) => onChange({ existing_dilution_concentration_dose_unit: s || null })} />
    <NumberField label="max_dose_per_hour" value={details.max_dose_per_hour ?? null} onChange={(n) => onChange({ max_dose_per_hour: n })} />
    <TextAreaField label="comment" value={details.comment ?? ''} onChange={(s) => onChange({ comment: s || null })} />
  </div>
);

const TextField: React.FC<{ label: string; value: string; onChange: (s: string) => void }> = ({ label, value, onChange }) => (
  <div className="resus-admin__field">
    <label className="resus-admin__field-label">{label}</label>
    <input className="resus-admin__input" type="text" value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

const TextAreaField: React.FC<{ label: string; value: string; onChange: (s: string) => void }> = ({ label, value, onChange }) => (
  <div className="resus-admin__field" style={{ gridColumn: '1 / -1' }}>
    <label className="resus-admin__field-label">{label}</label>
    <textarea className="resus-admin__textarea" rows={2} value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

export default CasesEditor;
